import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import {
  FileSearch,
  ClipboardCheck,
  Layers,
  Box,
  Factory,
  ShieldCheck,
  Truck,
  HeadphonesIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const steps = [
  { key: 'step1', number: '01', icon: FileSearch },
  { key: 'step2', number: '02', icon: ClipboardCheck },
  { key: 'step3', number: '03', icon: Layers },
  { key: 'step4', number: '04', icon: Box },
  { key: 'step5', number: '05', icon: Factory },
  { key: 'step6', number: '06', icon: ShieldCheck },
  { key: 'step7', number: '07', icon: Truck },
  { key: 'step8', number: '08', icon: HeadphonesIcon },
]

interface TimelineCardProps {
  step: (typeof steps)[0]
  index: number
}

function TimelineCard({ step, index }: TimelineCardProps) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })
  const Icon = step.icon

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="relative h-full"
    >
      {/* Node indicator */}
      <motion.div
        initial={{ scale: 0 }}
        animate={isInView ? { scale: 1 } : {}}
        transition={{ duration: 0.4, delay: index * 0.1 + 0.1, type: 'spring', stiffness: 200 }}
        className="absolute -top-3 -left-3 z-10"
      >
        <div
          className={cn(
            'w-8 h-8 rounded-full',
            'bg-primary/70',
            'flex items-center justify-center',
            'shadow-lg',
            'text-dark text-xs font-bold'
          )}
        >
          {step.number}
        </div>
      </motion.div>

      <div
        className={cn(
          'group relative bg-white rounded-xl p-5 lg:p-6 h-full',
          'shadow-[0_4px_20px_rgba(0,0,0,0.08)]',
          'hover:shadow-[0_8px_40px_rgba(0,0,0,0.12)]',
          'transition-all duration-500 ease-out',
          'border border-gray-100/50',
          'overflow-hidden flex flex-col'
        )}
      >

        {/* Icon */}
        <div
          className={cn(
            'inline-flex items-center justify-center',
            'w-10 h-10 rounded-lg mb-3',
            'bg-primary',
            'shadow-md',
            'transform group-hover:scale-110 group-hover:rotate-3',
            'transition-all duration-500'
          )}
        >
          <Icon className="w-5 h-5 text-dark" strokeWidth={1.5} />
        </div>

        {/* Title */}
        <h3 className="text-base lg:text-lg font-bold text-dark mb-2 group-hover:text-primary transition-colors duration-300">
          {t(`method.steps.${step.key}.title`)}
        </h3>

        {/* Description */}
        <p className="text-gray-600 text-sm leading-relaxed">
          {t(`method.steps.${step.key}.description`)}
        </p>
      </div>
    </motion.div>
  )
}

export function MethodTimeline() {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { once: true, margin: '-100px' })

  // Split steps for U layout
  const leftSteps = steps.slice(0, 2)    // 01, 02
  const bottomSteps = steps.slice(2, 6)  // 03, 04, 05, 06
  const rightSteps = steps.slice(6, 8)   // 07, 08

  return (
    <div ref={containerRef} className="relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      {/* Section Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8 md:mb-12"
      >
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
          <Factory className="w-4 h-4" />
          {t('method.badge')}
        </span>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-dark mb-4">
          {t('method.hero.title')}
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {t('method.subtitle')}
        </p>
      </motion.div>

      {/* U-Shaped Timeline Layout */}
      <div className="relative max-w-6xl mx-auto">
        {/* Top section with image and side cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-8">
          {/* Left side - Steps 1 & 2 */}
          <div className="lg:col-span-2 flex flex-col gap-6 order-2 lg:order-1">
            {leftSteps.map((step, idx) => (
              <div key={step.key} className="h-full">
                <TimelineCard step={step} index={idx} />
              </div>
            ))}
          </div>

          {/* Center - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-8 order-1 lg:order-2 px-4"
          >
            <div className="relative flex items-center justify-center">
              <img
                src="/images/animations/infografica_metodo.gif"
                alt={t('method.hero.title')}
                className="w-full max-w-3xl mx-auto rounded-2xl shadow-2xl"
              />
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-primary/10 to-transparent pointer-events-none" />
            </div>
          </motion.div>

          {/* Right side - Steps 7 & 8 (inverted order for U-shape symmetry) */}
          <div className="lg:col-span-2 flex flex-col gap-6 order-3">
            {[...rightSteps].reverse().map((step, idx) => (
              <div key={step.key} className="h-full">
                <TimelineCard step={step} index={idx + 6} />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section - Steps 3, 4, 5, 6 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 px-4 lg:px-12">
          {bottomSteps.map((step, idx) => (
            <div key={step.key} className="h-full">
              <TimelineCard step={step} index={idx + 2} />
            </div>
          ))}
        </div>

        {/* U-Shape connector lines (decorative) */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none hidden lg:block"
          style={{ zIndex: -1 }}
        >
          <motion.path
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ duration: 2, delay: 0.5, ease: 'easeInOut' }}
            d="M 100 80 Q 100 200 200 200 L 400 200 Q 600 200 700 200 Q 800 200 800 80"
            fill="none"
            stroke="url(#gradient)"
            strokeWidth="2"
            strokeDasharray="8 4"
          />
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FAB529" stopOpacity="0.4" />
              <stop offset="50%" stopColor="#FAB529" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FAB529" stopOpacity="0.4" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Bottom CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="text-center mt-12 md:mt-16"
      >
        <div className="inline-flex items-center gap-3 px-6 py-4 bg-dark rounded-2xl shadow-xl">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-dark" />
          </div>
          <div className="text-left">
            <p className="text-white font-semibold text-sm">{t('method.qualityBadge.title')}</p>
            <p className="text-white/60 text-xs">{t('method.qualityBadge.subtitle')}</p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
